"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import HeaderPage from "@/components/header-page";
import RestaurantCard from "@/components/restaurant-card"; // You'll need to create this component
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Filter, Utensils, X, ChevronDown, ChevronUp, Sparkles, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslations, useLocale } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';

interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

interface RestaurantApiResponse {
  restaurants: {
    current_page: number;
    data: any[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLink[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  };
}

interface FilterOptions {
  destinations: any[];
  categories: Array<{
    label: string;
    code: string;
    forkCount: number;
  }>;
}

// Component to render fork rating display
const ForkRatingDisplay = ({ forkCount, t }: { forkCount: number; t: any }) => {
  return (
    <div className="flex items-center">
      {[...Array(3)].map((_, i) => (
        <Utensils
          key={i}
          className={`h-4 w-4 ${
            i < forkCount 
              ? "fill-amber-600 text-amber-600" 
              : "text-gray-300"
          }`}
        />
      ))}
      <span className="ml-2 text-sm">
        {forkCount} {forkCount === 1 ? t('common.fork') : t('common.forks')}
      </span>
    </div>
  );
};

export default function Restaurants() {
  const t = useTranslations();
  const locale = useLocale();
  const { getLocalizedField } = useLocalizedField();
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // State for restaurants data
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<RestaurantApiResponse['restaurants'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filter states - initialized as empty, will be set from URL params
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("all");
  const [selectedCategoryCode, setSelectedCategoryCode] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    destinations: [],
    categories: []
  });

  // Fork categories with codes (1-3 forks)
  const getForkCategories = () => [
    { label: `1 ${t('common.fork')}`, code: "1", forkCount: 1 },
    { label: `2 ${t('common.forks')}`, code: "2", forkCount: 2 },
    { label: `3 ${t('common.forks')}`, code: "3", forkCount: 3 },
  ];

  // Build query parameters for API
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") {
      params.append("destination_id", selectedDestination);
    }
    if (selectedCategoryCode !== "all") params.append("fork_rating", selectedCategoryCode);
    
    return params;
  };

  // Build URL parameters for UI
  const buildUrlParams = () => {
    const params = new URLSearchParams();
    
    // Only add page if not 1 (cleaner URL)
    if (currentPage !== 1) params.append("page", currentPage.toString());
    
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") params.append("destination", selectedDestination);
    if (selectedCategoryCode !== "all") {
      params.append("category_code", selectedCategoryCode);
    }
    
    return params;
  };

  // Update URL when filters change (but not on initial load)
  const updateUrl = () => {
    if (isInitialLoad) return;
    
    const params = buildUrlParams();
    const queryString = params.toString();
    const newUrl = queryString ? `/restaurants?${queryString}` : '/restaurants';
    
    router.push(newUrl, { scroll: false });
  };

  // Fetch filter options (destinations from /destinations API and categories from restaurants)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch destinations from /destinations endpoint
        const destinationsResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}alldestinations`);
        const destinationsData = await destinationsResponse.json();
        
        const destinationsList: any[] = destinationsData.destinations || [];
        
        // Sort destinations alphabetically by name only
        const sortedDestinations = destinationsList.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        // Fetch first page of restaurants to get categories
        const restaurantsResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}restaurants?page=1`);
        const restaurantsData: RestaurantApiResponse = await restaurantsResponse.json();
        
        // Process categories from restaurants
        const categoryMap = new Map<string, { label: string; code: string; forkCount: number }>();
        
        restaurantsData.restaurants.data.forEach((restaurant: any) => {
          if (restaurant.category || restaurant.fork_rating) {
            const forkRating = restaurant.fork_rating || restaurant.category;
            const forkMatch = forkRating.toString().match(/(\d+)/);
            if (forkMatch) {
              const forkNum = parseInt(forkMatch[1]);
              if (forkNum >= 1 && forkNum <= 3) {
                const code = forkNum.toString();
                const label = `${forkNum} ${forkNum === 1 ? 'fork' : 'forks'}`;
                
                categoryMap.set(code, {
                  label,
                  code,
                  forkCount: forkNum
                });
              }
            } else {
              const code = (restaurant.category || '').toLowerCase().replace(/\s+/g, '_');
              categoryMap.set(code, {
                label: restaurant.category || 'Unknown',
                code,
                forkCount: 0
              });
            }
          }
        });
        
        // Add fork categories
        getForkCategories().forEach(forkCat => {
          if (!categoryMap.has(forkCat.code)) {
            categoryMap.set(forkCat.code, forkCat);
          }
        });
        
        setFilterOptions({
          destinations: sortedDestinations,
          categories: Array.from(categoryMap.values()).sort((a, b) => {
            if (a.forkCount > 0 && b.forkCount > 0) {
              return a.forkCount - b.forkCount;
            }
            return a.label.localeCompare(b.label);
          })
        });
        setLoadingFilters(false);
      } catch (error) {
        console.error("Error fetching filter options:", error);
        setLoadingFilters(false);
      }
    };
    
    fetchFilterOptions();
  }, []);

  // Initialize state from URL params on first render
  useEffect(() => {
    if (!searchParams || !isInitialLoad) return;
    
    const pageParam = searchParams.get("page");
    const searchParam = searchParams.get("search");
    const destinationParam = searchParams.get("destination");
    const categoryParam = searchParams.get("category_code");
    
    if (pageParam) {
      const page = parseInt(pageParam);
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page);
      }
    }
    
    if (searchParam) setSearchQuery(searchParam);
    if (destinationParam) setSelectedDestination(destinationParam);
    if (categoryParam) setSelectedCategoryCode(categoryParam);
    
    setIsInitialLoad(false);
  }, [searchParams, isInitialLoad]);

  // Update URL when filters change
  useEffect(() => {
    updateUrl();
  }, [currentPage, searchQuery, selectedDestination, selectedCategoryCode]);

  // Fetch restaurants based on filters
  useEffect(() => {
    const fetchRestaurants = async () => {
      if (isInitialLoad || loadingFilters) return;
      
      setLoading(true);
      try {
        const apiParams = buildQueryParams();
        
        // Fetch data from API
        const apiUrl = `${process.env.NEXT_PUBLIC_API_URL}restaurants?${apiParams.toString()}`;
        const response = await fetch(apiUrl);
        const data: RestaurantApiResponse = await response.json();
        
        setRestaurants(data.restaurants.data);
        setPaginationData(data.restaurants);
      } catch (error) {
        console.error("Error fetching restaurants:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRestaurants();
  }, [currentPage, searchQuery, selectedDestination, selectedCategoryCode, isInitialLoad, loadingFilters]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle filter changes
  const handleDestinationChange = (value: string) => {
    setSelectedDestination(value);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategoryCode(value);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle pagination - extract page number from URL
  const handlePageChange = (url: string | null) => {
    if (!url) return;
    
    try {
      const urlObj = new URL(url);
      const pageParam = urlObj.searchParams.get("page");
      if (pageParam) {
        const page = parseInt(pageParam);
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error parsing page URL:", error);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDestination("all");
    setSelectedCategoryCode("all");
    setCurrentPage(1);
    setShowMobileFilters(false);
    
    // Update URL to clean state
    router.push('/restaurants', { scroll: false });
  };

  // Clear single filter
  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearDestination = () => {
    setSelectedDestination("all");
    setCurrentPage(1);
  };

  const clearCategory = () => {
    setSelectedCategoryCode("all");
    setCurrentPage(1);
  };

  // Get selected category info for display
  const getSelectedCategoryInfo = () => {
    if (selectedCategoryCode === "all") return null;
    
    const allCategories = [...getForkCategories(), ...filterOptions.categories];
    return allCategories.find(cat => cat.code === selectedCategoryCode);
  };

  // Get selected destination name for display
  const getSelectedDestinationName = () => {
    if (selectedDestination === "all") return null;
    const destination = filterOptions.destinations.find(d => d.slug === selectedDestination);
    return destination ? destination.name : selectedDestination;
  };

  // Function to render pagination links with mobile optimization
  const renderPaginationLinks = () => {
    if (!paginationData || !paginationData.links) return null;

    const currentPage = paginationData.current_page;
    const lastPage = paginationData.last_page;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    
    // For mobile, show only: Previous | Current | Next
    if (isMobile && lastPage > 3) {
      const links = [];
      
      // Previous button
      links.push(
        <PaginationItem key="prev">
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(paginationData?.prev_page_url || null);
            }}
            className={`gap-1 px-2 py-2 min-w-[44px] min-h-[44px] flex items-center justify-center ${
              !paginationData?.prev_page_url
                ? "pointer-events-none opacity-50"
                : ""
            }`}
          >
            {locale === "ar" ? (
              <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
            ) : (
              <ChevronLeftIcon className="h-4 w-4 flex-shrink-0" />
            )}
          </PaginationLink>
        </PaginationItem>
      );
      
      // Current page
      links.push(
        <PaginationItem key="current">
          <PaginationLink
            href="#"
            isActive={true}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-sm font-semibold"
          >
            {currentPage}
          </PaginationLink>
        </PaginationItem>
      );
      
      // Page info
      links.push(
        <PaginationItem key="info">
          <span className="px-2 py-2 text-xs text-gray-500 flex items-center">
            / {lastPage}
          </span>
        </PaginationItem>
      );
      
      // Next button
      links.push(
        <PaginationItem key="next">
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(paginationData?.next_page_url || null);
            }}
            className={`gap-1 px-2 py-2 min-w-[44px] min-h-[44px] flex items-center justify-center ${
              !paginationData?.next_page_url
                ? "pointer-events-none opacity-50"
                : ""
            }`}
          >
            {locale === "ar" ? (
              <ChevronLeftIcon className="h-4 w-4 flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
            )}
          </PaginationLink>
        </PaginationItem>
      );
      
      return links;
    }

    // Desktop version - show all pagination links
    return paginationData.links.map((link, index) => {
      if (link.label === "...") {
        return (
          <PaginationItem key={index}>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const isPrevious = link.label.includes("previous");
      const isNext = link.label.includes("next");
      
      if (isPrevious) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.prev_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.prev_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              ) : (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (isNext) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.next_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.next_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                </>
              ) : (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      const pageNum = parseInt(link.label);
      if (!isNaN(pageNum)) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(link.url);
              }}
              isActive={link.active}
              className="min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center text-sm sm:text-base"
            >
              {pageNum}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return null;
    });
  };

  const selectedCategoryInfo = getSelectedCategoryInfo();

  // Filter sidebar component
  const FilterSidebar = () => (
    <div className="bg-card rounded-xl p-2 shadow-sm border border-border space-y-2">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground">{t('restaurants.filters')}</h2>
      </div>

      {/* Search Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2 flex items-center">
          {t('restaurants.search')} :
        </h3>
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Input
              placeholder={t('restaurants.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-3"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Destination Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2 flex items-center">
          {t('restaurants.filterByDestination')} :
        </h3>
        <Select
          value={selectedDestination}
          onValueChange={handleDestinationChange}
          disabled={loadingFilters}
        >
          <SelectTrigger className="w-full flex items-center">
            <SelectValue placeholder={t('restaurants.allDestinations')}>
              {selectedDestination !== "all"
                ? getSelectedDestinationName()
                : t('restaurants.allDestinations')}
            </SelectValue>

            {selectedDestination !== "all" && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearDestination();
                }}
                className="ml-2"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </SelectTrigger>

          <SelectContent className="max-h-[400px] overflow-y-auto">
            <SelectGroup>
              <SelectItem value="all" className="font-semibold py-3">
                {t('restaurants.allDestinations')}
              </SelectItem>

              {filterOptions.destinations.map((destination) => (
                <SelectItem
                  key={destination.id}
                  value={destination.slug}
                  className="py-2 text-sm text-gray-600"
                >
                  {getLocalizedField(destination, 'name')}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Category Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2 flex items-center">
          {t('restaurants.forkRating')}
        </h3>

        <div className="space-y-3">
          {/* ALL Ratings */}
          <button
            onClick={() => handleCategoryChange("all")}
            className={`w-full flex items-center justify-between p-2 rounded-lg border transition 
              ${
                selectedCategoryCode === "all"
                  ? "bg-primary text-white"
                  : "bg-gray-50"
              }
            `}
          >
            <span className="font-medium">{t('restaurants.allRatings')}</span>
          </button>

          {/* 1 → 3 Forks */}
          {[1, 2, 3].map((fork) => (
            <button
              key={fork}
              onClick={() => handleCategoryChange(String(fork))}
              className={`w-full flex items-center justify-between p-2 rounded-lg border transition 
                ${
                  selectedCategoryCode === String(fork)
                    ? "bg-blue-600 text-white"
                    : "bg-gray-50"
                }
              `}
            >
              <div className="flex items-center">
                {[...Array(3)].map((_, i) => (
                  <Utensils
                    key={i}
                    className={`h-4 w-4 ${
                      i < fork
                        ? selectedCategoryCode === String(fork)
                          ? "fill-white text-white"
                          : "fill-amber-600 text-amber-600"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>

              <span className="text-sm">
                {fork} {fork === 1 ? t('common.fork') : t('common.forks')}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Active Filters */}
      {(selectedDestination !== "all" ||
        selectedCategoryCode !== "all" ||
        searchQuery) && (
        <div className="pt-4 border-t space-y-4">
          <h3 className="text-lg font-semibold">{t('restaurants.activeFilters')}</h3>

          <div className="space-y-2">
            {searchQuery && (
              <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                <span className="text-sm text-blue-700">
                  {t('restaurants.search')}: "{searchQuery}"
                </span>
                <button
                  onClick={clearSearch}
                  className="text-blue-500 hover:text-blue-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {selectedDestination !== "all" && (
              <div className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-md">
                <span className="text-sm text-green-700">
                  {t('restaurants.destination')}: {getSelectedDestinationName()}
                </span>
                <button
                  onClick={clearDestination}
                  className="text-green-500 hover:text-green-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {selectedCategoryInfo && (
              <div className="flex items-center justify-between bg-purple-50 px-3 py-2 rounded-md">
                <span className="text-sm text-purple-700">
                  {selectedCategoryInfo.forkCount > 0 ? (
                    <ForkRatingDisplay
                      forkCount={selectedCategoryInfo.forkCount}
                      t={t}
                    />
                  ) : (
                    `${t('restaurants.category')}: ${selectedCategoryInfo.label}`
                  )}
                </span>
                <button
                  onClick={clearCategory}
                  className="text-purple-500 hover:text-purple-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="w-full"
          >
            {t('restaurants.clearAllFilters')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <HeaderPage
        title={t('restaurants.title')}
        breadcrumbs={[
          { label: t('common.home'), href: "/" },
          { label: t('restaurants.title'), href: "/restaurants" },
        ]}
      />
      
      <div className="container mx-auto px-4 py-8">
        {/* Mobile Filter Button and Active Filters */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {t('restaurants.filters')}
              {showMobileFilters ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            
            {/* Active filters chips for mobile */}
            <div className="flex gap-2 overflow-x-auto">
              {selectedDestination !== "all" && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  {getSelectedDestinationName() || selectedDestination}
                  <X className="h-3 w-3 ml-1 cursor-pointer" onClick={clearDestination} />
                </span>
              )}
              {selectedCategoryCode !== "all" && selectedCategoryInfo && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                  {selectedCategoryInfo.label}
                  <X className="h-3 w-3 ml-1 cursor-pointer" onClick={clearCategory} />
                </span>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          {/* <Card className="mb-4">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('restaurants.searchPlaceholderMobile')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-gray-300"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-3 top-3"
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                </div>
                <Button type="submit">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card> */}

          {/* Mobile Filters Dropdown */}
          {showMobileFilters && (
            <Card className="mb-4">
              <CardContent className="p-4">
                <FilterSidebar />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Filters (Desktop) */}
          <div className="hidden lg:block lg:w-1/4 border-r-2 border-gray-200 pr-8">
            <div className="sticky top-24">
              <FilterSidebar />
            </div>
          </div>

          {/* Right Content - Restaurants List */}
          <div className="lg:w-3/4">
            {/* Desktop Search (hidden on mobile) */}
            <div className="hidden lg:block mb-6">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('restaurants.searchPlaceholderMobile')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-10"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </form>
            </div>

            {/* Results Count */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                {loading ? t('restaurants.loadingRestaurants') : `${t('restaurants.found')} ${paginationData?.total || 0} ${t('restaurants.restaurantsCount')}`}
              </h2>
              {paginationData && !loading && (
                <p className="text-gray-600">
                  {t('restaurants.showing')} {paginationData.from} {t('restaurants.to')} {paginationData.to} {t('restaurants.of')} {paginationData.total} {t('restaurants.results')}
                  {paginationData.current_page && ` (${t('restaurants.page')} ${paginationData.current_page} ${t('restaurants.of')} ${paginationData.last_page})`}
                </p>
              )}
            </div>
            
            {/* Restaurants Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i}>
                    <Skeleton className="h-48 w-full" />
                    <div className="p-4">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-2/3 mb-4" />
                      <div className="flex justify-between">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : restaurants.length === 0 ? (
              <div className="flex flex-col gap-1 justify-center items-center">
                <div className="text-gray-400 mb-4">
                  <Utensils className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('restaurants.noRestaurantsFound')}</h3>
                <p className="text-gray-500 mb-6">{t('restaurants.tryAdjustingFilters')}</p>
                <Button onClick={clearFilters}>{t('restaurants.clearAllFilters')}</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                  {restaurants.map((restaurant) => (
                    <RestaurantCard
                      key={restaurant.id}
                      item={restaurant}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {paginationData && paginationData.last_page > 1 && (
                  <div className="mt-8 sm:mt-12">
                    <Pagination className="w-full">
                      <PaginationContent className={`flex flex-wrap justify-center gap-1 sm:gap-2 ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
                        {renderPaginationLinks()}
                      </PaginationContent>
                    </Pagination>
                    
                    <div className="mt-3 sm:mt-4 text-center text-xs sm:text-sm text-foreground dark:text-gray-400">
                      <p className={locale === 'ar' ? 'font-arabic' : ''}>
                        {t('restaurants.page')} {paginationData.current_page} {t('restaurants.of')} {paginationData.last_page}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}